import Dexie, { type EntityTable } from 'dexie';
import { PolarstepsTrip } from '../types/polarsteps';
import { PhotoRecord, MatchingConfidence } from '../types/domain';

// ----------------------------------------------------------------------------
// Type Definitions
// ----------------------------------------------------------------------------

export interface TripRecord extends PolarstepsTrip {
  updatedAt: number;
}

export interface BlobRecord {
  key: string;       // Primary Key: e.g. "thumb_abc123.webp", "prev_abc123.webp"
  blob: Blob;        // Raw binary WebP
  mimeType: string;  // "image/webp"
  size: number;      // File size in bytes
  createdAt: number; // Unix epoch ms
}

export interface StorageMetaRecord {
  key: string;       // e.g. "storage_mode", "active_trip_id"
  value: unknown;
  updatedAt: number;
}

export interface StorageFilter {
  tripId?: string | number;
  category?: string;
  timeRange?: [number, number]; // [startUtc, endUtc]
  confidence?: MatchingConfidence;
}

export interface StorageStats {
  tripCount: number;
  photoCount: number;
  blobCount: number;
  estimatedBytes: number;
  isOpfsActive: boolean;
  quotaBytes?: number;
  usageBytes?: number;
}

export interface StorageService {
  init(): Promise<void>;
  saveTrip(trip: PolarstepsTrip): Promise<void>;
  getTrip(id: number | string): Promise<PolarstepsTrip | undefined>;
  getAllTrips(): Promise<PolarstepsTrip[]>;
  deleteTrip(id: number | string): Promise<void>;
  savePhoto(record: PhotoRecord, thumbBlob: Blob, previewBlob: Blob): Promise<void>;
  savePhotosBatch(
    items: Array<{ record: PhotoRecord; thumbBlob: Blob; previewBlob: Blob }>,
    onProgress?: (saved: number, total: number) => void
  ): Promise<void>;
  updatePhotoMetadataBatch(records: PhotoRecord[]): Promise<void>;
  getPhotos(filter?: StorageFilter): Promise<PhotoRecord[]>;
  getPhoto(id: string): Promise<PhotoRecord | undefined>;
  getPhotoBlob(key: string): Promise<Blob | undefined>;
  getPhotoUrl(key: string): Promise<string | undefined>;
  revokePhotoUrl(key: string): void;
  deletePhoto(id: string): Promise<void>;
  getCategories(): Promise<string[]>;
  getStats(): Promise<StorageStats>;
  requestPersistentStorage(): Promise<boolean>;
  clearAll(): Promise<void>;
}

// ----------------------------------------------------------------------------
// Dexie Database Class
// ----------------------------------------------------------------------------

export class TravelPhotoMapDatabase extends Dexie {
  trips!: EntityTable<TripRecord, 'id'>;
  photos!: EntityTable<PhotoRecord, 'id'>;
  blobs!: EntityTable<BlobRecord, 'key'>;
  meta!: EntityTable<StorageMetaRecord, 'key'>;

  constructor(dbName = 'TravelPhotoMapDB') {
    super(dbName);

    this.version(1).stores({
      trips: 'id, name, start_date, end_date, updatedAt',
      photos:
        'id, category, timestampUtc, matchingConfidence, [category+timestampUtc], [matchingConfidence+timestampUtc]',
      blobs: 'key, mimeType, createdAt',
      meta: 'key',
    });

    this.version(2).stores({
      photos:
        'id, tripId, category, timestampUtc, matchingConfidence, [category+timestampUtc], [matchingConfidence+timestampUtc]',
    });
  }
}

// ----------------------------------------------------------------------------
// OPFS File System Access & Active Capability Probe
// ----------------------------------------------------------------------------

const OPFS_ROOT_DIR = 'splendid_photos';
const OPFS_THUMB_DIR = 'thumbnails';
const OPFS_PREV_DIR = 'previews';

let cachedOpfsSupport: boolean | null = null;

export function resetOpfsSupportCache(): void {
  cachedOpfsSupport = null;
}

export function setMockOpfsSupport(value: boolean | null): void {
  cachedOpfsSupport = value;
}

/**
 * Actively probes OPFS for real write/read capability.
 */
export async function isOpfsAvailable(): Promise<boolean> {
  if (cachedOpfsSupport !== null) return cachedOpfsSupport;

  try {
    if (typeof navigator === 'undefined' || !navigator.storage?.getDirectory) {
      cachedOpfsSupport = false;
      return false;
    }

    const root = await navigator.storage.getDirectory();
    const probeFileName = `.probe_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;

    const probeHandle = await root.getFileHandle(probeFileName, { create: true });

    if (typeof probeHandle.createWritable !== 'function') {
      try {
        await root.removeEntry(probeFileName);
      } catch {}
      cachedOpfsSupport = false;
      return false;
    }

    const writable = await probeHandle.createWritable();
    await writable.write(new Uint8Array([1, 2, 3]));
    await writable.close();

    const file = await probeHandle.getFile();
    if (file.size !== 3) {
      try {
        await root.removeEntry(probeFileName);
      } catch {}
      cachedOpfsSupport = false;
      return false;
    }

    // Cleanup probe artifact
    await root.removeEntry(probeFileName);
    cachedOpfsSupport = true;
    return true;
  } catch {
    cachedOpfsSupport = false;
    return false;
  }
}

async function getOpfsDirectory(
  pathSegments: string[],
  create = false
): Promise<FileSystemDirectoryHandle | null> {
  if (typeof navigator === 'undefined' || !navigator.storage?.getDirectory) return null;
  try {
    let current = await navigator.storage.getDirectory();
    for (const segment of pathSegments) {
      current = await current.getDirectoryHandle(segment, { create });
    }
    return current;
  } catch {
    return null;
  }
}

async function writeOpfsFile(folder: string, filename: string, blob: Blob): Promise<boolean> {
  try {
    const dir = await getOpfsDirectory([OPFS_ROOT_DIR, folder], true);
    if (!dir) return false;
    const fileHandle = await dir.getFileHandle(filename, { create: true });
    const writable = await fileHandle.createWritable();
    try {
      await writable.write(blob);
    } finally {
      await writable.close();
    }
    return true;
  } catch {
    return false;
  }
}

async function readOpfsFile(folder: string, filename: string): Promise<Blob | undefined> {
  try {
    const dir = await getOpfsDirectory([OPFS_ROOT_DIR, folder], false);
    if (!dir) return undefined;
    const fileHandle = await dir.getFileHandle(filename, { create: false });
    return await fileHandle.getFile();
  } catch {
    return undefined;
  }
}

async function deleteOpfsFile(folder: string, filename: string): Promise<boolean> {
  try {
    const dir = await getOpfsDirectory([OPFS_ROOT_DIR, folder], false);
    if (!dir) return false;
    await dir.removeEntry(filename);
    return true;
  } catch {
    return false;
  }
}

async function clearOpfsStorage(): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.storage?.getDirectory) return;
  try {
    const root = await navigator.storage.getDirectory();
    await root.removeEntry(OPFS_ROOT_DIR, { recursive: true });
  } catch (err: any) {
    if (err.name !== 'NotFoundError') {
      // Non-fatal if folder doesn't exist yet
    }
  }
}

// ----------------------------------------------------------------------------
// Bounded LRU Object URL Manager
// ----------------------------------------------------------------------------

export class BlobUrlManager {
  private urlMap = new Map<string, { url: string; lastUsed: number }>();
  private accessSeq = 0;
  private readonly maxActiveUrls: number;

  constructor(maxActiveUrls = 100) {
    this.maxActiveUrls = maxActiveUrls;
  }

  get activeCount(): number {
    return this.urlMap.size;
  }

  getExisting(key: string): string | undefined {
    const entry = this.urlMap.get(key);
    if (entry) {
      entry.lastUsed = ++this.accessSeq;
      return entry.url;
    }
    return undefined;
  }

  create(key: string, blob: Blob): string {
    const existing = this.getExisting(key);
    if (existing) return existing;

    // Enforce LRU eviction if capacity reached
    if (this.urlMap.size >= this.maxActiveUrls) {
      let oldestKey: string | null = null;
      let oldestTime = Infinity;

      for (const [k, v] of this.urlMap.entries()) {
        if (v.lastUsed < oldestTime) {
          oldestTime = v.lastUsed;
          oldestKey = k;
        }
      }

      if (oldestKey) {
        this.revoke(oldestKey);
      }
    }

    if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
      const url = URL.createObjectURL(blob);
      this.urlMap.set(key, { url, lastUsed: ++this.accessSeq });
      return url;
    }

    // Fallback for environments lacking URL.createObjectURL
    const fallbackUrl = `blob:mock-${key}-${Date.now()}`;
    this.urlMap.set(key, { url: fallbackUrl, lastUsed: ++this.accessSeq });
    return fallbackUrl;
  }

  revoke(key: string): void {
    const entry = this.urlMap.get(key);
    if (entry) {
      if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(entry.url);
      }
      this.urlMap.delete(key);
    }
  }

  revokeAll(): void {
    for (const entry of this.urlMap.values()) {
      if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(entry.url);
      }
    }
    this.urlMap.clear();
  }
}

// ----------------------------------------------------------------------------
// Canonical Chronological Sorter
// ----------------------------------------------------------------------------

/**
 * Canonical chronological sorter for PhotoRecord collections.
 * Orders strictly by timestampUtc ascending (time of shot).
 * Breaks ties using dateTimeOriginal string, then fileName.
 */
export function sortPhotosChronologically(photos: PhotoRecord[]): PhotoRecord[] {
  return [...photos].sort((a, b) => {
    const timeA = typeof a.timestampUtc === 'number' && !Number.isNaN(a.timestampUtc) ? a.timestampUtc : 0;
    const timeB = typeof b.timestampUtc === 'number' && !Number.isNaN(b.timestampUtc) ? b.timestampUtc : 0;
    if (timeA !== timeB) {
      return timeA - timeB;
    }
    const dtA = a.dateTimeOriginal || '';
    const dtB = b.dateTimeOriginal || '';
    if (dtA !== dtB) {
      return dtA.localeCompare(dtB);
    }
    return (a.fileName || '').localeCompare(b.fileName || '');
  });
}

// ----------------------------------------------------------------------------
// Hybrid Storage Service Implementation
// ----------------------------------------------------------------------------

export class HybridStorageService implements StorageService {
  private db: TravelPhotoMapDatabase;
  private urlManager: BlobUrlManager;
  private opfsEnabled = false;
  private initialized = false;

  constructor(customDb?: TravelPhotoMapDatabase) {
    this.db = customDb ?? new TravelPhotoMapDatabase();
    this.urlManager = new BlobUrlManager(100);
  }

  get database(): TravelPhotoMapDatabase {
    return this.db;
  }

  get isOpfsActive(): boolean {
    return this.opfsEnabled;
  }

  get activeUrlCount(): number {
    return this.urlManager.activeCount;
  }

  async init(): Promise<void> {
    if (this.initialized) return;
    this.opfsEnabled = await isOpfsAvailable();
    if (typeof indexedDB !== 'undefined') {
      try {
        if (!this.db.isOpen()) {
          await this.db.open();
        }
        await this.db.meta.put({
          key: 'engine_mode',
          value: this.opfsEnabled ? 'OPFS_PRIMARY' : 'INDEXEDDB_FALLBACK',
          updatedAt: Date.now(),
        });
      } catch (err) {
        console.warn('[Storage] IndexedDB initialization bypassed:', err);
      }
    }
    this.initialized = true;
  }

  private resolveKeyParts(key: string): { folder: string; filename: string } {
    if (key.startsWith('thumb_')) {
      return { folder: OPFS_THUMB_DIR, filename: key };
    }
    if (key.startsWith('prev_')) {
      return { folder: OPFS_PREV_DIR, filename: key };
    }
    return { folder: 'misc', filename: key };
  }

  // --- Trips ---

  async saveTrip(trip: PolarstepsTrip): Promise<void> {
    await this.init();
    const record: TripRecord = {
      ...trip,
      updatedAt: Date.now(),
    };
    await this.db.trips.put(record);
  }

  async getTrip(id: number | string): Promise<PolarstepsTrip | undefined> {
    await this.init();
    return await this.db.trips.get(id);
  }

  async getAllTrips(): Promise<PolarstepsTrip[]> {
    await this.init();
    return await this.db.trips.toArray();
  }

  async deleteTrip(id: number | string): Promise<void> {
    await this.init();
    await this.db.trips.delete(id);
    const photos = await this.db.photos.toArray();
    const tripPhotos = photos.filter((p) => p.tripId === id);
    for (const p of tripPhotos) {
      await this.deletePhoto(p.id);
    }
  }

  async updatePhotoMetadataBatch(records: PhotoRecord[]): Promise<void> {
    await this.init();
    if (records.length === 0) return;
    await this.db.photos.bulkPut(records);
  }

  // --- Photos ---

  async savePhoto(record: PhotoRecord, thumbBlob: Blob, previewBlob: Blob): Promise<void> {
    await this.init();

    const thumbKey = record.thumbnailKey || `thumb_${record.id}.webp`;
    const prevKey = record.previewKey || `prev_${record.id}.webp`;

    const updatedRecord: PhotoRecord = {
      ...record,
      thumbnailKey: thumbKey,
      previewKey: prevKey,
      thumbnailSize: thumbBlob.size,
      previewSize: previewBlob.size,
    };

    // 1. Binary persistence (OPFS primary or IndexedDB fallback)
    let wroteThumbOpfs = false;
    let wrotePrevOpfs = false;

    if (this.opfsEnabled) {
      const thumbParts = this.resolveKeyParts(thumbKey);
      const prevParts = this.resolveKeyParts(prevKey);
      wroteThumbOpfs = await writeOpfsFile(thumbParts.folder, thumbParts.filename, thumbBlob);
      wrotePrevOpfs = await writeOpfsFile(prevParts.folder, prevParts.filename, previewBlob);
    }

    // Fallback to IndexedDB Blobs if OPFS is unavailable or write failed
    if (!wroteThumbOpfs) {
      await this.db.blobs.put({
        key: thumbKey,
        blob: thumbBlob,
        mimeType: thumbBlob.type || 'image/webp',
        size: thumbBlob.size,
        createdAt: Date.now(),
      });
    }

    if (!wrotePrevOpfs) {
      await this.db.blobs.put({
        key: prevKey,
        blob: previewBlob,
        mimeType: previewBlob.type || 'image/webp',
        size: previewBlob.size,
        createdAt: Date.now(),
      });
    }

    // 2. Relational metadata persistence
    await this.db.photos.put(updatedRecord);
  }

  async savePhotosBatch(
    items: Array<{ record: PhotoRecord; thumbBlob: Blob; previewBlob: Blob }>,
    onProgress?: (saved: number, total: number) => void
  ): Promise<void> {
    await this.init();
    if (items.length === 0) return;

    const concurrencyLimit = 6;
    const recordsToPut: PhotoRecord[] = [];
    const fallbackBlobsToPut: BlobRecord[] = [];

    // Process binary writes with bounded concurrency
    for (let i = 0; i < items.length; i += concurrencyLimit) {
      const slice = items.slice(i, i + concurrencyLimit);

      await Promise.all(
        slice.map(async ({ record, thumbBlob, previewBlob }) => {
          const thumbKey = record.thumbnailKey || `thumb_${record.id}.webp`;
          const prevKey = record.previewKey || `prev_${record.id}.webp`;

          const updatedRecord: PhotoRecord = {
            ...record,
            thumbnailKey: thumbKey,
            previewKey: prevKey,
            thumbnailSize: thumbBlob.size,
            previewSize: previewBlob.size,
          };
          recordsToPut.push(updatedRecord);

          let wroteThumb = false;
          let wrotePrev = false;

          if (this.opfsEnabled) {
            const thumbParts = this.resolveKeyParts(thumbKey);
            const prevParts = this.resolveKeyParts(prevKey);
            [wroteThumb, wrotePrev] = await Promise.all([
              writeOpfsFile(thumbParts.folder, thumbParts.filename, thumbBlob),
              writeOpfsFile(prevParts.folder, prevParts.filename, previewBlob),
            ]);
          }

          if (!wroteThumb) {
            fallbackBlobsToPut.push({
              key: thumbKey,
              blob: thumbBlob,
              mimeType: thumbBlob.type || 'image/webp',
              size: thumbBlob.size,
              createdAt: Date.now(),
            });
          }

          if (!wrotePrev) {
            fallbackBlobsToPut.push({
              key: prevKey,
              blob: previewBlob,
              mimeType: previewBlob.type || 'image/webp',
              size: previewBlob.size,
              createdAt: Date.now(),
            });
          }
        })
      );

      if (onProgress) {
        onProgress(Math.min(i + slice.length, items.length), items.length);
      }
    }

    // Single transaction for metadata & fallback blobs
    await this.db.transaction('rw', [this.db.photos, this.db.blobs], async () => {
      await this.db.photos.bulkPut(recordsToPut);
      if (fallbackBlobsToPut.length > 0) {
        await this.db.blobs.bulkPut(fallbackBlobsToPut);
      }
    });
  }

  async getPhotos(filter?: StorageFilter): Promise<PhotoRecord[]> {
    await this.init();

    let records: PhotoRecord[];

    // 1. Both category and time range specified -> compound index [category+timestampUtc]
    if (filter?.category && filter.category !== 'Tutte' && filter?.timeRange) {
      const [start, end] = filter.timeRange;
      const query = this.db.photos
        .where('[category+timestampUtc]')
        .between([filter.category, start], [filter.category, end], true, true);

      if (filter.confidence) {
        records = await query.filter((p) => p.matchingConfidence === filter.confidence).toArray();
      } else {
        records = await query.toArray();
      }
    }
    // 2. Specific category only
    else if (filter?.category && filter.category !== 'Tutte') {
      const query = this.db.photos.where('category').equals(filter.category);
      if (filter.confidence) {
        records = await query.filter((p) => p.matchingConfidence === filter.confidence).toArray();
      } else {
        records = await query.toArray();
      }
    }
    // 3. Time range only
    else if (filter?.timeRange) {
      const [start, end] = filter.timeRange;
      const query = this.db.photos.where('timestampUtc').between(start, end, true, true);
      if (filter.confidence) {
        records = await query.filter((p) => p.matchingConfidence === filter.confidence).toArray();
      } else {
        records = await query.toArray();
      }
    }
    // 4. Confidence filter only
    else if (filter?.confidence) {
      records = await this.db.photos.where('matchingConfidence').equals(filter.confidence).toArray();
    }
    // 5. No filter: all photos
    else {
      records = await this.db.photos.toArray();
    }

    if (filter?.tripId) {
      records = records.filter((p) => {
        if (p.tripId) return p.tripId === filter.tripId;
        // Legacy photos without tripId default to islanda-trip or active trip
        return (
          filter.tripId === 'islanda-trip' ||
          filter.tripId === 'stored-active-trip' ||
          String(filter.tripId).includes('islanda')
        );
      });
    }

    return sortPhotosChronologically(records);
  }

  async getPhoto(id: string): Promise<PhotoRecord | undefined> {
    await this.init();
    return await this.db.photos.get(id);
  }

  // --- Blob & URL Retrieval ---

  async getPhotoBlob(key: string): Promise<Blob | undefined> {
    try {
      await this.init();

      // 1. Check OPFS if active
      if (this.opfsEnabled) {
        const parts = this.resolveKeyParts(key);
        const blob = await readOpfsFile(parts.folder, parts.filename);
        if (blob) return blob;
      }

      // 2. Fallback to Dexie blobs table
      if (typeof indexedDB !== 'undefined' && this.db && this.db.isOpen()) {
        const blobRecord = await this.db.blobs.get(key);
        return blobRecord?.blob;
      }
      return undefined;
    } catch {
      return undefined;
    }
  }

  async getPhotoUrl(key: string): Promise<string | undefined> {
    try {
      if (!key) return undefined;
      if (
        key.startsWith('http://') ||
        key.startsWith('https://') ||
        key.startsWith('/') ||
        key.startsWith('photos/') ||
        key.startsWith('data:') ||
        key.startsWith('blob:')
      ) {
        return key;
      }

      const existing = this.urlManager.getExisting(key);
      if (existing) return existing;

      const blob = await this.getPhotoBlob(key);
      if (!blob) {
        if (key.startsWith('thumb_')) {
          const id = key.replace(/^thumb_/, '').replace(/\.webp$/, '');
          return `/photos/thumb/${id}.webp`;
        }
        if (key.startsWith('prev_')) {
          const id = key.replace(/^prev_/, '').replace(/\.webp$/, '');
          return `/photos/hd/${id}.webp`;
        }
        return undefined;
      }

      return this.urlManager.create(key, blob);
    } catch {
      return undefined;
    }
  }

  revokePhotoUrl(key: string): void {
    this.urlManager.revoke(key);
  }

  async deletePhoto(id: string): Promise<void> {
    await this.init();
    const photo = await this.db.photos.get(id);
    if (!photo) return;

    const thumbKey = photo.thumbnailKey || `thumb_${id}.webp`;
    const prevKey = photo.previewKey || `prev_${id}.webp`;

    this.urlManager.revoke(thumbKey);
    this.urlManager.revoke(prevKey);

    if (this.opfsEnabled) {
      const thumbParts = this.resolveKeyParts(thumbKey);
      const prevParts = this.resolveKeyParts(prevKey);
      await Promise.all([
        deleteOpfsFile(thumbParts.folder, thumbParts.filename),
        deleteOpfsFile(prevParts.folder, prevParts.filename),
      ]);
    }

    await this.db.transaction('rw', [this.db.photos, this.db.blobs], async () => {
      await this.db.photos.delete(id);
      await this.db.blobs.delete(thumbKey);
      await this.db.blobs.delete(prevKey);
    });
  }

  async getCategories(): Promise<string[]> {
    await this.init();
    const photos = await this.db.photos.toArray();
    const unique = new Set<string>();
    for (const p of photos) {
      if (p.category) unique.add(p.category);
    }
    return Array.from(unique).sort();
  }

  async getStats(): Promise<StorageStats> {
    await this.init();
    const [tripCount, photoCount, blobCount] = await Promise.all([
      this.db.trips.count(),
      this.db.photos.count(),
      this.db.blobs.count(),
    ]);

    let quotaBytes: number | undefined;
    let usageBytes: number | undefined;

    if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
      try {
        const est = await navigator.storage.estimate();
        quotaBytes = est.quota;
        usageBytes = est.usage;
      } catch {}
    }

    const photos = await this.db.photos.toArray();
    const estimatedBytes = photos.reduce(
      (sum, p) => sum + (p.thumbnailSize || 50000) + (p.previewSize || 180000),
      0
    );

    return {
      tripCount,
      photoCount,
      blobCount,
      estimatedBytes,
      isOpfsActive: this.opfsEnabled,
      quotaBytes,
      usageBytes,
    };
  }

  async requestPersistentStorage(): Promise<boolean> {
    if (typeof navigator !== 'undefined' && navigator.storage?.persist) {
      try {
        return await navigator.storage.persist();
      } catch {
        return false;
      }
    }
    return false;
  }

  async clearAll(): Promise<void> {
    await this.init();
    this.urlManager.revokeAll();

    if (this.opfsEnabled) {
      await clearOpfsStorage();
    }

    await Promise.all([
      this.db.trips.clear(),
      this.db.photos.clear(),
      this.db.blobs.clear(),
      this.db.meta.clear(),
    ]);
  }

  async close(): Promise<void> {
    this.urlManager.revokeAll();
    if (this.db.isOpen()) {
      this.db.close();
    }
    this.initialized = false;
  }
}

export const db = new TravelPhotoMapDatabase();
export const storageService = new HybridStorageService(db);
